import classNames from 'classnames';
import * as inflection from 'inflection';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import * as styles from './ThreadVersionChangeCard.module.scss';
import VersionChangeCard from './VersionChangeCard';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import PopOver from '../../components/deprecated/PopOver';
import SVGIcon from '../../components/SVGIcon';
import Toast from '../../components/Toast';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import { UIActions } from '../../ui/uiSlice';
import { useGetUser } from '../../user/useAllUsers';
import {
  CommentAuthor,
  CommentContainer,
  CommentContent,
  CommentLeft,
  CommentMeta,
  CommentRight,
} from '../comment/Comment';
import CommentPreview from '../comment/CommentPreview';
import CommentThreadCompactAvatarStack, {
  OverflowCount,
} from '../comment/CommentThreadCompactAvatarStack';
import { CommentThreadFullContainer } from '../comment/CommentThreadFull';
import CommentTimestamp from '../comment/CommentTimestamp';
import FoldedComments from '../comment/FoldedComments';
import useResolveThread from '../comment/useResolveThread';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import { useMagneticWallLayout } from '../MagneticWallLayout';
import useUnresolveThread from '../utils/useUnresolveThread';

const COMMENT_FRAGMENT = gql(`
  fragment ThreadVersionChangeCard_comment on Comment {
    id
    content
    author {
      ...useGetUser_user
    }
    insertedAt
    updatedAt
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ThreadVersionChangeCard_thread on CommentThread {
    id
    resolvedAt
    resolver {
      id
    }
    comments {
      id
      insertedAt
      author {
        ...useGetUser_user
      }
      ...ThreadVersionChangeCard_comment
    }
  }
`);

const COMMENT_LIMIT = 2;
const MAX_LINE_COUNT = 3;

interface VersionChangeCommentProps {
  historyInsertedAt: Date;
  comment: DocumentType<typeof COMMENT_FRAGMENT>;
  expanded: boolean;
  onResize: () => void;
  onCopyLink: () => void;
}

const VersionChangeComment = ({
  historyInsertedAt,
  comment,
  expanded,
  onResize,
  onCopyLink,
}: VersionChangeCommentProps) => {
  const commentContentRef = useRef<HTMLDivElement>(null);
  const getUser = useGetUser();
  const author = getUser(comment.author);

  if (!comment) return null;

  const avatarWithNewBadge = (
    <VersionChangeCard.NewBadgeContainer
      showBadge={new Date(comment.insertedAt) >= historyInsertedAt}
    >
      <MagneticWallItemAvatar user={author} />
    </VersionChangeCard.NewBadgeContainer>
  );

  return (
    <CommentContainer>
      <CommentLeft>
        <FoldedComments.LineContainer>
          {expanded ? (
            <VersionChangeCard.CopyLinkButton onCopyLink={onCopyLink}>
              {avatarWithNewBadge}
            </VersionChangeCard.CopyLinkButton>
          ) : (
            avatarWithNewBadge
          )}
        </FoldedComments.LineContainer>
      </CommentLeft>
      <CommentRight>
        {expanded && (
          <CommentMeta>
            <CommentAuthor>{author.name}</CommentAuthor>
            <CommentTimestamp
              insertedAt={comment.insertedAt}
              updatedAt={comment.updatedAt}
              noTooltip
            />
          </CommentMeta>
        )}
        <CommentContent ref={commentContentRef}>
          <CommentPreview
            initialHeight={commentContentRef.current?.clientHeight}
            collapseLines={!expanded}
            content={comment.content}
            link={expanded}
            showHoverCard={expanded}
            maxLines={MAX_LINE_COUNT}
            disableLineClamp={expanded}
            onResize={onResize}
          />
        </CommentContent>
      </CommentRight>
    </CommentContainer>
  );
};

export type ThreadVersionChange = {
  id: string;
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  type: 'thread';
};

interface ThreadVersionChangeCardProps {
  focused: boolean;
  versionChange: ThreadVersionChange;
  historyInsertedAt: Date;
  onFocus: () => void;
  onHighlight: () => void;
  onRemoveHighlight: () => void;
  magneticWallItemSize: MagneticWallItemSize;
  canEdit: boolean;
  onCopyLink: () => void;
}

const ThreadVersionChangeCard = ({
  focused,
  versionChange: { thread },
  historyInsertedAt,
  onFocus,
  onHighlight,
  onRemoveHighlight,
  magneticWallItemSize,
  canEdit,
  onCopyLink,
}: ThreadVersionChangeCardProps) => {
  const dispatch = useAppDispatch();
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);

  const getUser = useGetUser();
  const { updateThreadHeights, layout, handlePopOverRender } =
    useMagneticWallLayout();

  const resolveThread = useResolveThread(thread);
  const handleResolveThread = async () => {
    await resolveThread();

    // this avoids showing Thread unresolved and Thread resolved
    // at the same time to not offer invalid undo action
    dispatch(UIActions.clearToasts());
    dispatch(
      UIActions.pushToast(({ onDismiss }) => (
        <Toast
          level="success"
          onDismiss={onDismiss}
          message="Thread resolved"
          secondaryText="Undo"
          onSecondaryClick={() => {
            onDismiss();
            handleUnresolveThread();
          }}
        />
      )),
    );
  };

  const unresolveThread = useUnresolveThread();
  const handleUnresolveThread = async () => {
    await unresolveThread(thread);

    // this avoids showing Thread unresolved and Thread resolved
    // at the same time to not offer invalid undo action
    dispatch(UIActions.clearToasts());
    dispatch(
      UIActions.pushToast(({ onDismiss }) => (
        <Toast
          level="success"
          onDismiss={onDismiss}
          message="Thread unresolved"
          secondaryText="Undo"
          onSecondaryClick={() => {
            onDismiss();
            handleResolveThread();
          }}
        />
      )),
    );
  };

  const fullThread = (
    <>
      <div
        className={classNames(styles.content, {
          [styles.resolved]: thread.resolvedAt != null,
          [styles.focused]: focused,
        })}
      >
        <CommentThreadFullContainer expanded={focused} showThread>
          <FoldedComments.Root
            comments={thread.comments}
            limit={COMMENT_LIMIT}
            renderMoreText={(count) => {
              const newComments = thread.comments
                .slice(1, thread.comments.length - (COMMENT_LIMIT - 1))
                .filter(
                  (comment) =>
                    new Date(comment.insertedAt) >= historyInsertedAt,
                );

              if (newComments.length > 0) {
                if (newComments.length === count) {
                  return (
                    <span className={styles.newCommentsText}>
                      {count} more new {inflection.inflect('comment', count)}
                    </span>
                  );
                }
                return (
                  <span>
                    {count} more {inflection.inflect('comment', count)}{' '}
                    <span className={styles.newCommentsText}>
                      ({newComments.length} new)
                    </span>
                  </span>
                );
              }

              return `${count} more ${inflection.inflect('comment', count)}`;
            }}
            expanded={focused}
            showLineWhenFolded
            renderComment={(comment) => (
              <VersionChangeComment
                expanded={focused}
                comment={comment}
                onResize={updateThreadHeights}
                historyInsertedAt={historyInsertedAt}
                onCopyLink={onCopyLink}
              />
            )}
          />
        </CommentThreadFullContainer>
      </div>
      {focused && thread.resolvedAt && (
        <div className={styles.footer}>
          <span className={styles.resolvedThreadTextContainer}>
            <SVGIcon>{checkIcon}</SVGIcon>
            <span className={styles.resolvedThreadText}>
              Resolved at {moment(thread.resolvedAt).format('MMM Do, h:mma')}
            </span>
          </span>
          {canEdit && (
            <Tooltip
              trigger="clickOrHover"
              content={
                thread.resolver == null
                  ? 'Cannot unresolve comment\nbecause the commented text\nhas been deleted from the post'
                  : null
              }
            >
              <Button
                variant="link"
                size="small"
                className={styles.unresolveButton}
                disabled={thread.resolver == null}
                onClick={handleUnresolveThread}
              >
                Unresolve
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </>
  );

  const hasTwoNewCommentsNextToEachOther =
    thread.comments.filter(
      (item) => new Date(item.insertedAt) >= historyInsertedAt,
    ).length === 2 && thread.comments.length === 2;

  const hasNewCommentInTheMiddle = thread.comments
    .slice(1, -1)
    .some((item) => new Date(item.insertedAt) >= historyInsertedAt);

  return (
    <MagneticWallItem.Root
      id={thread.id}
      focused={focused}
      size={magneticWallItemSize}
      onMouseEnter={() => {
        onHighlight();
      }}
      onMouseLeave={() => {
        onRemoveHighlight();
      }}
      compactView={
        <MagneticWallItem.ResponsivePopover
          onClose={() => setIsPopOverOpen(false)}
          target={
            <VersionChangeCard.Root focused={focused}>
              <CommentThreadCompactAvatarStack
                minOverflow={1}
                maxAvatarsShown={
                  hasTwoNewCommentsNextToEachOther ? 2 : undefined
                }
                renderOverflowCount={(overflowCount) => {
                  const shouldShowSuccess =
                    hasTwoNewCommentsNextToEachOther ||
                    hasNewCommentInTheMiddle;

                  // we won't ever show a badge, but it will have
                  // position relative, and all items in the stack need it
                  // so that they are in the same stacking context
                  return (
                    <VersionChangeCard.NewBadgeContainer showBadge={false}>
                      <OverflowCount
                        overflowCount={overflowCount}
                        variant={shouldShowSuccess ? 'success' : 'accent'}
                      />
                    </VersionChangeCard.NewBadgeContainer>
                  );
                }}
                items={thread.comments}
              >
                {(item) => (
                  <VersionChangeCard.NewBadgeContainer
                    showBadge={new Date(item.insertedAt) >= historyInsertedAt}
                  >
                    <VersionChangeCard.CompactAvatarContainer>
                      <MagneticWallItemAvatar user={getUser(item.author)} />
                    </VersionChangeCard.CompactAvatarContainer>
                  </VersionChangeCard.NewBadgeContainer>
                )}
              </CommentThreadCompactAvatarStack>
            </VersionChangeCard.Root>
          }
          onPopOverHeightChange={handlePopOverRender.bind(null, thread.id)}
          content={
            <PopOver.Container noPadding width="auto" fullHeight>
              {fullThread}
            </PopOver.Container>
          }
          compactPopOverSide={layout.compactPopOverSide}
          open={isPopOverOpen}
        />
      }
      onClick={() => {
        if (!focused) {
          onFocus();
        }

        if (magneticWallItemSize === 'compact') {
          setIsPopOverOpen(true);
        }
      }}
      fullView={
        <VersionChangeCard.Root focused={focused}>
          <MagneticWallItem.Card focused={focused} overlapped={false} focusable>
            {fullThread}
          </MagneticWallItem.Card>
        </VersionChangeCard.Root>
      }
    />
  );
};

export default ThreadVersionChangeCard;
